import React from "react";
import moment from "moment";
import { alpha, makeStyles } from "@material-ui/core/styles";
import { Button, IconButton, Tooltip } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import VisibilityIcon from "@material-ui/icons/VisibilityOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import SendIcon from "@material-ui/icons/Send";

import ContactAvatar from "../../components/ContactAvatar";
import formatPhone from "../../helpers/formatPhone";
import { attentionReason, relativeTime } from "../../helpers/scheduleStatus";

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const danger = dark ? "#FF8A80" : "#B3261E";
  return {
    card: {
      display: "flex",
      gap: theme.spacing(1.75),
      alignItems: "flex-start",
      padding: theme.spacing(1.75, 2),
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 12,
      "&:hover": { borderColor: theme.palette.text.disabled },
      [theme.breakpoints.down("xs")]: {
        flexWrap: "wrap",
        padding: theme.spacing(1.5),
        gap: theme.spacing(1.25),
      },
    },
    cardAttention: {
      borderColor: alpha("#E53935", dark ? 0.6 : 0.4),
      backgroundColor: dark ? "rgba(229,57,53,0.08)" : "rgba(229,57,53,0.04)",
    },
    when: {
      width: 64,
      flexShrink: 0,
      [theme.breakpoints.down("xs")]: { width: 48 },
    },
    time: { fontSize: 15, fontWeight: 700, lineHeight: "20px" },
    timeAttention: { color: danger },
    rel: { fontSize: 12, color: theme.palette.text.secondary },
    avatar: {
      width: 36,
      height: 36,
      fontSize: 14,
      flexShrink: 0,
      [theme.breakpoints.down("xs")]: { display: "none" },
    },
    main: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4 },
    nameRow: { display: "flex", alignItems: "baseline", gap: theme.spacing(1), flexWrap: "wrap" },
    name: { fontSize: 15, fontWeight: 600 },
    phone: { fontSize: 12, color: theme.palette.text.secondary },
    body: {
      fontSize: 14,
      lineHeight: "20px",
      color: theme.palette.text.primary,
      opacity: 0.85,
      wordBreak: "break-word",
      display: "-webkit-box",
      WebkitBoxOrient: "vertical",
      WebkitLineClamp: 2,
      overflow: "hidden",
    },
    reason: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontSize: 13,
      fontWeight: 500,
      color: danger,
    },
    meta: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 2 },
    tag: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      height: 24,
      padding: "0 8px",
      borderRadius: 6,
      fontSize: 12,
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.secondary,
    },
    tagInfo: {
      backgroundColor: alpha(theme.palette.primary.main, dark ? 0.2 : 0.1),
      color: dark ? "#CFE0FA" : theme.palette.primary.dark,
    },
    tagSent: {
      backgroundColor: alpha("#2E8B47", dark ? 0.25 : 0.12),
      color: dark ? "#A5D6A7" : "#1E6B33",
    },
    actions: {
      display: "flex",
      gap: 4,
      alignItems: "center",
      flexShrink: 0,
      [theme.breakpoints.down("xs")]: {
        width: "100%",
        justifyContent: "flex-end",
        borderTop: `1px solid ${theme.palette.divider}`,
        paddingTop: theme.spacing(1),
      },
    },
    button: { textTransform: "none", fontWeight: 600, borderRadius: 9, height: 34 },
    delete: { color: theme.palette.error.main },
  };
});

// Um agendamento na lista. `state` vem de scheduleState().
const ScheduleCard = ({ schedule, state, onOpen, onDelete, onSendNow, onDuplicate }) => {
  const classes = useStyles();
  const attention = state === "attention";
  const sent = state === "sent";
  const contactName = schedule.contact?.name || "Sem contato";

  return (
    <div className={`${classes.card} ${attention ? classes.cardAttention : ""}`}>
      <div className={classes.when}>
        <div className={`${classes.time} ${attention ? classes.timeAttention : ""}`}>
          {moment(schedule.sendAt).format("HH:mm")}
        </div>
        <div className={classes.rel}>
          {sent ? moment(schedule.sendAt).format("DD/MM") : relativeTime(schedule.sendAt)}
        </div>
      </div>

      <ContactAvatar name={contactName} className={classes.avatar} />

      <div className={classes.main}>
        <div className={classes.nameRow}>
          <span className={classes.name}>{contactName}</span>
          {schedule.contact?.number && (
            <span className={classes.phone}>{formatPhone(schedule.contact.number)}</span>
          )}
        </div>
        <div className={classes.body}>{schedule.body}</div>
        {attention && (
          <div className={classes.reason}>
            <ErrorOutlineIcon style={{ fontSize: 16 }} />
            {attentionReason(schedule)}
          </div>
        )}
        <div className={classes.meta}>
          {sent && (
            <span className={`${classes.tag} ${classes.tagSent}`}>
              Enviada{schedule.sentAt ? ` em ${moment(schedule.sentAt).format("DD/MM HH:mm")}` : ""}
            </span>
          )}
          {schedule.mediaName && (
            <span className={classes.tag}>
              <AttachFileIcon style={{ fontSize: 13 }} />
              {schedule.mediaName}
            </span>
          )}
          {schedule.keepTicketOpen && (
            <span className={`${classes.tag} ${classes.tagInfo}`}>
              <ChatBubbleOutlineIcon style={{ fontSize: 13 }} />
              Abre atendimento para a resposta
            </span>
          )}
          {schedule.user?.name && (
            <span className={classes.tag}>por {schedule.user.name}</span>
          )}
        </div>
      </div>

      <div className={classes.actions}>
        {attention && (
          <>
            <Button
              variant="outlined"
              className={classes.button}
              onClick={() => onOpen(schedule)}
            >
              Reagendar
            </Button>
            <Button
              variant="contained"
              color="primary"
              disableElevation
              className={classes.button}
              startIcon={<SendIcon style={{ fontSize: 16 }} />}
              onClick={() => onSendNow(schedule)}
            >
              Enviar agora
            </Button>
          </>
        )}
        {state === "pending" && (
          <Tooltip title="Enviar agora">
            <IconButton size="small" aria-label="Enviar agora" onClick={() => onSendNow(schedule)}>
              <SendIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {!attention && (
          <Tooltip title={sent ? "Ver mensagem" : "Editar"}>
            <IconButton
              size="small"
              aria-label={sent ? "Ver mensagem" : "Editar agendamento"}
              onClick={() => onOpen(schedule)}
            >
              {sent ? <VisibilityIcon fontSize="small" /> : <EditIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Duplicar">
          <IconButton size="small" aria-label="Duplicar agendamento" onClick={() => onDuplicate(schedule)}>
            <FileCopyOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Excluir">
          <IconButton
            size="small"
            aria-label="Excluir agendamento"
            className={classes.delete}
            onClick={() => onDelete(schedule)}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
};

export default ScheduleCard;
